import { FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import ViewToggle from 'components/v2/common/viewToggle';

import useTranslate from 'hooks/useTranslate';
import type { OwnershipScope, ProviderOption } from 'types/servicesDocuments';

interface Props {
    isSuperSuperAdmin: boolean;
    ownershipScope: OwnershipScope;
    onOwnershipScopeChange: (scope: OwnershipScope) => void;
    providerId: number | '';
    onProviderIdChange: (id: number | '') => void;
    providerOptions: ProviderOption[];
    providersLoading?: boolean;
    fixedProviderLabel?: string;
}

const ScopeToolbar = ({
    isSuperSuperAdmin,
    ownershipScope,
    onOwnershipScopeChange,
    providerId,
    onProviderIdChange,
    providerOptions,
    providersLoading = false,
    fixedProviderLabel,
}: Props) => {
    const { translate } = useTranslate();

    if (!isSuperSuperAdmin) {
        return (
            <Typography variant="body2" color="text.secondary" data-testid="fixed-provider-label">
                {fixedProviderLabel
                    ? `${translate('servicesDocuments.scope.provider')}: ${fixedProviderLabel}`
                    : translate('servicesDocuments.scope.currentProvider')}
            </Typography>
        );
    }

    const providerLabel = translate('servicesDocuments.scope.selectProvider');
    const showProviderSelect = ownershipScope === 'provider';
    const providerValue = providerId === '' ? '' : String(providerId);
    const providerValueInOptions =
        providerValue === '' ||
        providerOptions.some((option) => String(option.id) === providerValue);

    return (
        <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            useFlexGap
            sx={{
                alignItems: { xs: 'stretch', sm: 'center' },
                flexWrap: 'wrap',
            }}
        >
            <ViewToggle
                ariaLabel={translate('servicesDocuments.scope.ownershipAria')}
                value={ownershipScope}
                onChange={onOwnershipScopeChange}
                options={[
                    {
                        id: 'provider',
                        label: translate('company'),
                    },
                    {
                        id: 'universal',
                        label: translate('servicesDocuments.scope.universal'),
                    },
                ]}
            />

            {showProviderSelect ? (
                <FormControl
                    size="small"
                    disabled={providersLoading}
                    sx={{ width: { xs: '100%', sm: 345 }, flexShrink: 0 }}
                >
                    <InputLabel id="services-documents-provider-label">{providerLabel}</InputLabel>
                    <Select
                        labelId="services-documents-provider-label"
                        label={providerLabel}
                        value={providerValue}
                        onChange={(event) =>
                            onProviderIdChange(
                                event.target.value === '' ? '' : Number(event.target.value)
                            )
                        }
                    >
                        <MenuItem value="" sx={{ display: 'none' }} />
                        {!providerValueInOptions ? (
                            <MenuItem value={providerValue} sx={{ display: 'none' }} />
                        ) : null}
                        {providerOptions.map((option) => (
                            <MenuItem key={option.id} value={String(option.id)}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            ) : null}
        </Stack>
    );
};

export default ScopeToolbar;
